import React, { useState } from 'react';
import { Box, Button, Link, makeStyles, TextField, Typography } from '@material-ui/core';
import { format, isToday } from 'date-fns';
import ru from 'date-fns/locale/ru';
import { IClientCommentModel } from 'interfaces';

interface IProps {
  comment: IClientCommentModel;
  /** Максимальная длина текста — совпадает с ограничением колонки на бэке. */
  maxLength: number;
  /** Идёт запись на бэк: кнопки блокируются, чтобы не отправить действие дважды. */
  saving: boolean;
  editing: boolean;
  editValue: string;
  onEditValueChange: (value: string) => void;
  onEditStart: () => void;
  onEditCancel: () => void;
  onEditSave: () => void;
  onDelete: () => void;
}

// С этой длины текст сворачивается до четырёх строк с кнопкой «Показать полностью».
const CLIP_LENGTH = 190;

const useStyles = makeStyles(() => ({
  root: {
    border: '1px solid #e4e8ee',
    borderRadius: 6,
    padding: '11px 12px 10px',
    backgroundColor: '#fff',
    height: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
  },
  top: {
    display: 'flex',
    alignItems: 'flex-start',
    marginBottom: 7,
  },
  avatar: {
    width: 30,
    height: 30,
    flex: 'none',
    marginRight: 9,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#eef4fc',
    color: '#1668c1',
    fontSize: 11.5,
    fontWeight: 700,
  },
  who: {
    minWidth: 0,
    flex: 1,
  },
  name: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    fontSize: 13,
    fontWeight: 600,
    lineHeight: 1.25,
    color: '#1f2733',
  },
  tag: {
    marginLeft: 6,
    padding: '2px 6px',
    borderRadius: 3,
    backgroundColor: '#e8f5ec',
    color: '#2b6b3f',
    fontSize: 9.5,
    fontWeight: 700,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
  },
  meta: {
    fontSize: 11,
    color: '#98a2b3',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  body: {
    flexGrow: 1,
  },
  text: {
    fontSize: 13.5,
    lineHeight: 1.4,
    color: '#2b3441',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  // сворачивание длинного текста до четырёх строк
  textClip: {
    display: '-webkit-box',
    WebkitLineClamp: 4,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  linkButton: {
    fontSize: 12,
    lineHeight: 1.4,
    color: '#1668c1',
    '&:disabled': {
      color: '#98a2b3',
      cursor: 'default',
      textDecoration: 'none',
    },
  },
  more: {
    marginTop: 2,
  },
  actions: {
    display: 'flex',
    marginTop: 8,
    paddingTop: 7,
    borderTop: '1px solid #f1f3f7',
  },
  action: {
    marginRight: 14,
  },
  delete: {
    color: '#c62828',
  },
  confirm: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    marginTop: 8,
    padding: '7px 9px',
    borderRadius: 6,
    backgroundColor: '#fdeeee',
    border: '1px solid #f3cfcf',
  },
  confirmText: {
    fontSize: 12.5,
    color: '#8f2020',
    marginRight: 10,
  },
  confirmYes: {
    color: '#c62828',
    fontWeight: 600,
    marginRight: 10,
  },
  confirmNo: {
    color: '#6b7684',
  },
  editBar: {
    display: 'flex',
    marginTop: 8,
  },
  editButton: {
    marginRight: 8,
    textTransform: 'none',
  },
}));

/**
 * «сегодня, 09:14» для сегодняшних комментариев, «5 авг. 2026, 09:14» для остальных.
 * Бэк отдаёт момент в UTC (spring.jackson.serialization.write-dates-as-timestamps=false),
 * а date-fns форматирует по местному времени — время видно в часовом поясе пользователя.
 */
const formatMoment = (value?: string | null): string => {
  if (!value) return '';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  return isToday(date) ? `сегодня, ${format(date, 'HH:mm')}` : format(date, 'd MMM yyyy, HH:mm', { locale: ru });
};

const getAuthorName = (comment: IClientCommentModel): string => {
  const fullName = (comment.createByFullName || '').trim();
  return fullName || comment.createBy || '—';
};

const getInitials = (name: string): string => {
  const parts = name.split(/\s+/).filter(Boolean);
  if (!parts.length) return '—';
  return `${parts[0][0]}${parts[1] ? parts[1][0] : ''}`.toUpperCase();
};

/**
 * Один комментарий по клиенту: автор с аватаром и датой, текст (длинный сворачивается),
 * действия автора и администратора. Удаление подтверждается тут же в карточке, чтобы
 * не открывать диалог поверх карточки клиента.
 */
const CommentCard: React.FC<IProps> = ({
  comment,
  maxLength,
  saving,
  editing,
  editValue,
  onEditValueChange,
  onEditStart,
  onEditCancel,
  onEditSave,
  onDelete,
}) => {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const author = getAuthorName(comment);
  const clipped = comment.comment.length > CLIP_LENGTH && !expanded;
  const textClass = clipped ? `${classes.text} ${classes.textClip}` : classes.text;

  const renderBody = (): React.ReactNode => {
    if (editing) {
      return (
        <Box>
          <TextField
            fullWidth
            multiline
            autoFocus
            size="small"
            variant="outlined"
            minRows={3}
            maxRows={8}
            value={editValue}
            disabled={saving}
            inputProps={{ maxLength }}
            onChange={(event) => onEditValueChange(event.target.value)}
          />
          <Box className={classes.editBar}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              className={classes.editButton}
              disabled={saving || !editValue.trim()}
              onClick={onEditSave}
            >
              Сохранить
            </Button>
            <Button
              variant="outlined"
              size="small"
              className={classes.editButton}
              disabled={saving}
              onClick={onEditCancel}
            >
              Отмена
            </Button>
          </Box>
        </Box>
      );
    }

    return (
      <Box className={classes.body}>
        <Typography className={textClass}>{comment.comment}</Typography>
        {comment.comment.length > CLIP_LENGTH && (
          <Link
            component="button"
            type="button"
            className={`${classes.linkButton} ${classes.more}`}
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? 'Свернуть' : 'Показать полностью'}
          </Link>
        )}
      </Box>
    );
  };

  const renderActions = (): React.ReactNode => {
    if (editing || confirming) return null;

    return (
      <Box className={classes.actions}>
        {comment.canEdit && (
          <Link
            component="button"
            type="button"
            className={`${classes.linkButton} ${classes.action}`}
            disabled={saving}
            onClick={onEditStart}
          >
            Изменить
          </Link>
        )}
        {comment.canDelete && (
          <Link
            component="button"
            type="button"
            className={`${classes.linkButton} ${classes.action} ${classes.delete}`}
            disabled={saving}
            onClick={() => setConfirming(true)}
          >
            Удалить
          </Link>
        )}
      </Box>
    );
  };

  const renderConfirm = (): React.ReactNode => {
    if (!confirming) return null;

    // у чужого комментария в подтверждении называем автора — оператор должен видеть, что удаляет не своё
    const target = comment.canEdit ? '' : ` пользователя ${author.split(/\s+/)[0]}`;

    return (
      <Box className={classes.confirm}>
        <Typography className={classes.confirmText}>Удалить комментарий{target}?</Typography>
        <Link
          component="button"
          type="button"
          className={`${classes.linkButton} ${classes.confirmYes}`}
          disabled={saving}
          onClick={onDelete}
        >
          Удалить
        </Link>
        <Link
          component="button"
          type="button"
          className={`${classes.linkButton} ${classes.confirmNo}`}
          disabled={saving}
          onClick={() => setConfirming(false)}
        >
          Отмена
        </Link>
      </Box>
    );
  };

  return (
    <Box className={classes.root}>
      <Box className={classes.top}>
        <Box className={classes.avatar}>{getInitials(author)}</Box>
        <Box className={classes.who}>
          <Box className={classes.name}>
            {author}
            {comment.canEdit && <span className={classes.tag}>вы</span>}
          </Box>
          <Typography className={classes.meta}>
            {formatMoment(comment.createAt)}
            {comment.updateAt ? ` · изменено ${formatMoment(comment.updateAt)}` : ''}
          </Typography>
        </Box>
      </Box>

      {renderBody()}
      {renderActions()}
      {renderConfirm()}
    </Box>
  );
};

export default CommentCard;
